import * as IF from "infrontjs";
import template from "./template.html";

import { LogoutState } from "../../../states/auth/LogoutState";
import { LANGUAGES, _lcs, currentLanguage, languageLabel } from "../../../i18n";
import { guardBack } from "../../../core/BackDismiss";

/**
 * <menu-drawer> — the "Menü" side drawer.
 *
 * Opened from the bottom-nav's fifth tab via the View. Items with a `route`
 * navigate to that state; the "language" item expands into the language picker
 * in place, and so does "house" — the houses a signed-in guest has stayed at,
 * shown only when there are several (core/AccommodationSwitch); the rest are
 * present-but-not-yet-wired in this build and surface a toast. The whole drawer re-renders on open so the guest line always reflects
 * the current user.
 *
 * The titles and labels below are German source strings that double as their
 * dictionary keys — this is a module constant, evaluated before I18n exists, so
 * the translation happens in the template where they are read.
 */
const GROUPS = [
    { title: "Aufenthalt", items: [
        { id: "info",     icon: "book-open",     label: "Gästemappe",      route: "/info" },
        { id: "region",   icon: "map-pinned",    label: "Entdecken",       route: "/region" },
        { id: "events",   icon: "calendar-days", label: "Events",          route: "/events" },
        { id: "plan",     icon: "route",         label: "Planer",          route: "/plan" },
        { id: "feedback", icon: "star",          label: "Feedback geben",  route: "/feedback" }
    ] },
    { title: "Mehr", items: [
        // The meta is the CURRENT language, filled in at render time.
        { id: "language", icon: "languages",     label: "Sprache" },
        // Only with more than one house to choose from; the meta is the
        // CURRENT house.
        { id: "house",    icon: "building-2",    label: "Unterkunft" },
        { id: "settings", icon: "settings",      label: "Einstellungen",   route: "/settings" },
        // BELTURO's own pages on the website, opened in a new tab.
        { id: "privacy",  icon: "shield-check",  label: "Datenschutz",     url: "https://www.belturo.com/datenschutz" },
        { id: "legal",    icon: "file-text",     label: "Impressum",       url: "https://www.belturo.com/impressum" }
    ] }
];

class MenuDrawer extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._open = false;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this._connected = true;

            // The install card goes the moment the app is installed (or the
            // prompt is used up) — even while the drawer is open. Hidden rather
            // than re-rendered: a render would also drop the open state.
            this.app.addEventListener( "pwa-change", () =>
            {
                const card = this.querySelector( '[data-id="install"]' );
                if ( card ) card.hidden = !this.app.pwa.canInstall();
            } );
        }
    }

    render()
    {
        const user = this.app.user || {};
        const guestLine = user.guest
            ? _lcs( "Als Gast angemeldet" )
            : [ user.name, user.room ].filter( Boolean ).join( " · " ) || _lcs( "Willkommen" );

        this.app.view.render( this, template, {
            house     : this._houseName(),
            guestLine : guestLine,
            logoUrl   : this.app.accommodation?.theme?.logoUrl || null,
            groups    : GROUPS,
            languages : LANGUAGES,
            currentLanguage : currentLanguage(),
            currentLanguageLabel : languageLabel( currentLanguage() ),
            houses : this.app.accommodationSwitch.canSwitch() ? this.app.accommodationSwitch.houses : [],
            currentHouseId : this.app.accommodation?.id || null,
            canInstall : this.app.pwa ? this.app.pwa.canInstall() : false
        } );

        this.drawer = this.querySelector( '[data-id="drawer"]' );
        this.bind();
    }

    bind()
    {
        this.querySelector( '[data-id="scrim"]' )?.addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="close"]' )?.addEventListener( "click", () => this.close() );

        this.querySelector( '[data-id="logout"]' )?.addEventListener( "click", () =>
        {
            this.close();
            this.app.router.redirect( LogoutState.ROUTE );
        } );

        // Language: expand the list in place rather than closing the drawer.
        const langToggle = this.querySelector( '[data-id="lang-toggle"]' );
        const langList = this.querySelector( '[data-id="langs"]' );
        langToggle?.addEventListener( "click", () =>
        {
            const open = langList.hidden;
            langList.hidden = !open;
            langToggle.setAttribute( "aria-expanded", String( open ) );
        } );

        // Switching reloads the app (see App.switchLanguage), so there is
        // nothing to re-render here — picking the active language is a no-op.
        this.querySelectorAll( '[data-id="lang"]' ).forEach( ( btn ) =>
        {
            btn.addEventListener( "click", () =>
                this.app.switchLanguage( btn.getAttribute( "data-lang" ) ) );
        } );

        // Houses: expand in place like the languages. Picking another one
        // leaves for that house's guest-app (signed in); the current one is
        // a no-op.
        const houseToggle = this.querySelector( '[data-id="house-toggle"]' );
        const houseList = this.querySelector( '[data-id="houses"]' );
        houseToggle?.addEventListener( "click", () =>
        {
            const open = houseList.hidden;
            houseList.hidden = !open;
            houseToggle.setAttribute( "aria-expanded", String( open ) );
        } );

        this.querySelectorAll( '[data-id="house"]' ).forEach( ( btn ) =>
        {
            btn.addEventListener( "click", async () =>
            {
                const id = btn.getAttribute( "data-accommodation-id" );
                if ( id === this.app.accommodation?.id || btn.disabled ) return;

                btn.disabled = true;
                if ( !await this.app.accommodationSwitch.switchTo( id ) )
                {
                    btn.disabled = false;
                    this.app.view.toast( _lcs( "Der Wechsel hat nicht geklappt. Bitte versuche es erneut." ), "error" );
                }
            } );
        } );

        // Chromium: the browser's own install dialog, right away. iOS: Pwa
        // sends the guest to the install page's instructions instead.
        this.querySelector( '[data-id="install"]' )?.addEventListener( "click", () =>
        {
            this.close();
            this.app.pwa.install();
        } );

        // External links open in a new tab on their own; just close the drawer.
        this.querySelectorAll( '[data-id="external"]' ).forEach( ( link ) =>
        {
            link.addEventListener( "click", () => this.close() );
        } );

        // Every other item: navigate, or say the screen is not built yet.
        this.querySelectorAll( ".drawer__item[data-label]" ).forEach( ( btn ) =>
        {
            btn.addEventListener( "click", () =>
            {
                const route = btn.getAttribute( "data-route" );
                this.close();
                if ( route )
                {
                    this.app.router.redirect( route );
                }
                else
                {
                    this.app.view.toast(
                        _lcs( "{feature} ist bald verfügbar.", { feature : btn.getAttribute( "data-label" ) } ),
                        "info"
                    );
                }
            } );
        } );
    }

    open()
    {
        // Re-render to refresh the guest line, then animate in next frame.
        this.render();
        if ( !this._open )
        {
            // Back closes the menu rather than leaving the page (F19).
            this._releaseBack = guardBack( () => this.close( true ) );
        }
        this._open = true;
        requestAnimationFrame( () =>
        {
            this.drawer?.classList.add( "drawer--open" );
            this.drawer?.setAttribute( "aria-hidden", "false" );
        } );
    }

    /** @param {boolean} [byBack] Closed by the back button (BackDismiss). */
    close( byBack = false )
    {
        if ( !this._open ) return;
        this._open = false;
        if ( !byBack && this._releaseBack )
        {
            this._releaseBack();
        }
        this._releaseBack = null;
        this.drawer?.classList.remove( "drawer--open" );
        this.drawer?.setAttribute( "aria-hidden", "true" );
    }

    _houseName()
    {
        // The resolved accommodation is the source of truth; fall back to a
        // name derived from the subdomain slug (pure demo mode).
        if ( this.app.accommodation?.name )
        {
            return this.app.accommodation.name;
        }

        const slug = this.app.theme.getTenantSlug();
        if ( !slug )
        {
            return "BELTURO";
        }
        return slug
            .split( "-" )
            .map( ( w ) => w.charAt( 0 ).toUpperCase() + w.slice( 1 ) )
            .join( " " );
    }
}

customElements.define( "menu-drawer", MenuDrawer );
